/* eslint-disable no-case-declarations */

import { App } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications, Token } from '@capacitor/push-notifications';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { useQueryClient } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import { useAuth } from 'auth/AuthContext';
import PageLoader from 'components/loader/PageLoader';
import {
  insertConversation,
  insertUnassignedConversationFromMessage,
  removeConversation as removeConversationMutator,
  setConversationStatusValue,
  setOwnerForConversation as setOwnerForConversationMutator,
  updateConversationLastMessageIfMatch,
  updateConversationReadStatus as updateConversationReadStatusMutator,
  updateConversationWithNewMessage,
  upsertConversation,
} from 'hooks/conversationListMutators';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import { conversationMessagesQueryKey, suggestedAnswerQueryKey, useUpdateConversations } from 'hooks/useConversations';
import useInAppChatNotification, {
  IN_APP_CHAT_NOTIFICATION_EVENT,
  InAppChatNotificationDetail,
} from 'hooks/useInAppChatNotification';
import useIsAppVisible from 'hooks/useIsAppVisible';
import useLoadConversations from 'hooks/useLoadConversations';
import useScheduleArticleNotifications from 'hooks/useScheduleArticleNotifications';
import ActionTypes from 'notifications';
import { createNotificationChannels } from 'notifications/channels';
import Keys from 'preferences/preferences';
import posthog from 'posthog-js';
import {
  BETTER_NOTIFICATION_REQUEST_TIMING_FLAG,
  PushNotificationOptInProvider,
} from 'push/PushNotificationOptInContext';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import WebSocketService, {
  WebSocketDataChatMessage,
  WebSocketDataConversationAssigned,
  WebSocketDataConversationCreated,
  WebSocketDataConversationRequestToGroup,
  WebSocketDataConversationStatusUpdated,
  WebSocketDataMessageDeleted,
  WebSocketDataMessageEdited,
  WebSocketDataReadNotification,
  WebSocketDataReplySuggestionCreated,
} from 'services/WebSocketService';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { conversationActions } from 'store/conversations';
import { userActions } from 'store/users';
import { selectCurrentUser, selectCurrentUserId } from 'store/users/selectors';
import Platform from 'utils/Platform';
import syncBadgeCount from 'utils/syncBadgeCount';
import ActivityTracker from '../../components/ActivityTracker';
import BottomNavBar from '../../components/navigation/BottomNavBar';
import { getAndClearPendingLaunchNotification } from '../../pendingLaunchNotification';
import { routes } from '../../services/RouteService';
import { toast } from 'sonner';

export default function Shell() {
  // Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { status: authStatus, accessToken } = useAuth();

  // Selector
  const currentUserId = useSelector(selectCurrentUserId);
  const currentUser = useSelector(selectCurrentUser);

  useCheckNotificationsPermissions();
  useInAppChatNotification();
  // Mount the conversations list bootstrap fetch *once* for the whole app.
  // Each WebSocket reconnect re-runs the request inside this hook (gap recovery).
  // Individual messaging pages (Home, MessagingHome, ConversationPage) used to mount
  // this hook themselves, multiplying requests and producing flaky e2e races.
  useLoadConversations();
  // Single chokepoint to mutate the conversations list cached by TanStack Query;
  // every websocket handler below goes through it. The list is the single source of
  // truth for the inbox UI and is no longer mirrored in Redux.
  const updateConversations = useUpdateConversations();

  const isAppVisible = useIsAppVisible();

  const appStateResumed = useRef<boolean>(true);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  useEffect(() => {
    const appStateListener = App.addListener('appStateChange', ({ isActive }) => {
      appStateResumed.current = isActive;
    });

    let keyboardWillShowListener: Promise<PluginListenerHandle> | undefined;
    let keyboardWillHideListener: Promise<PluginListenerHandle> | undefined;
    if (Capacitor.isPluginAvailable('Keyboard')) {
      keyboardWillShowListener = Keyboard.addListener('keyboardWillShow', () => {
        setKeyboardVisible(true);
      });
      keyboardWillHideListener = Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardVisible(false);
      });
    }

    return () => {
      appStateListener.then(l => l.remove());
      keyboardWillShowListener?.then(l => l.remove());
      keyboardWillHideListener?.then(l => l.remove());
    };
  }, []);

  const { run: registerFCMToken } = useRequest(
    (fcmToken: string) => {
      return ApiUsersManager.registerFCMToken({ token: fcmToken });
    },
    {
      manual: true,
      onSuccess: (_, [fcmToken]) => {
        // Remembered so that logout can detach this device from the account
        Preferences.set({ key: Keys.PUSH_REGISTERED_FCM_TOKEN, value: fcmToken });
      },
      onError: () => {
        // TODO better error?
      },
    },
  );

  const scheduleArticleReminders = () => {
    if (posthog.getFeatureFlag('prevention-program') === 'automatic') {
      scheduleInitialArticleReminders();
      rescheduleArticleRemindersFromNow();
    }
  };

  // Keeps the store in step with subscription changes the app did not initiate
  // — a renewal, an expiry, a purchase made on another device — so the settings
  // page reflects them without a restart. `addCustomerInfoUpdateListener`
  // returns the id needed to unregister it; discarding that id left a listener
  // behind on every remount, each one dispatching the same update (the leak
  // fixed elsewhere in #745).
  useEffect(() => {
    if (!Platform.isNative()) return;

    let callbackId: string | undefined;
    let removed = false;

    Purchases.addCustomerInfoUpdateListener(customerInfo => {
      dispatch(userActions.setCurrentUserSubscription(customerInfo));
    }).then(id => {
      callbackId = id;
      // The effect may already have been cleaned up while this was in flight.
      if (removed) Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: id });
    });

    return () => {
      removed = true;
      if (callbackId) Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: callbackId });
    };
  }, [dispatch]);

  const { scheduleInitialArticleReminders, rescheduleArticleRemindersFromNow } = useScheduleArticleNotifications();

  useEffect(() => {
    const localNotificationListenerHandles: Promise<PluginListenerHandle>[] = [];
    const pushNotificationListenerHandles: Promise<PluginListenerHandle>[] = [];

    if (authStatus === 'authenticated' && accessToken && currentUserId) {
      // Handle notification that launched the app when it was closed (cold start).
      // The listeners in pendingLaunchNotification run before Shell mounts, so we
      // process any stored tap here once the user is authenticated.
      const pending = getAndClearPendingLaunchNotification();
      if (pending) {
        if (pending.source === 'push') {
          const data = pending.action.notification.data as Record<string, unknown> | undefined;
          const pushNotificationId = data?.notification_id as string | undefined;
          const pushNotificationType = data?.notification_type as string | undefined;
          posthog.capture('push_notification.clicked', {
            notification_id: pushNotificationId,
            notification_type: pushNotificationType,
          });
          const navigateTo = data?.navigate_to as string | undefined;
          if (navigateTo) {
            navigate(navigateTo);
          }
        } else {
          const actionTypeId = pending.action.notification.actionTypeId;
          if (actionTypeId === ActionTypes.SYMPTOM_TRACKING_REMINDER) {
            posthog.capture('tracking.reminder_notification_clicked');
            navigate(routes.symptomTracking.route);
          } else if (actionTypeId === ActionTypes.PERIOD_REMINDER) {
            posthog.capture('tracking.period_reminder_notification_clicked');
            navigate(routes.symptomTracking.route);
          } else if (actionTypeId === ActionTypes.PREVENTION_JOURNEY) {
            const id = pending.action.notification.extra?.articleId ?? 1;
            const index = String(Math.max(1, id));
            navigate(routes.article.route.replace(':index', index));
            posthog.capture('prevention.article_reminder_notification_clicked', { articleIndex: index });
          }
        }
      }

      if (Capacitor.isPluginAvailable('LocalNotifications')) {
        localNotificationListenerHandles.push(
          LocalNotifications.addListener('localNotificationActionPerformed', notificationAction => {
            if (notificationAction.notification.actionTypeId === ActionTypes.SYMPTOM_TRACKING_REMINDER) {
              posthog.capture('tracking.reminder_notification_clicked');
              navigate(routes.symptomTracking.route);
            }
          }),
          LocalNotifications.addListener('localNotificationActionPerformed', notificationAction => {
            if (notificationAction.notification.actionTypeId === ActionTypes.PERIOD_REMINDER) {
              posthog.capture('tracking.period_reminder_notification_clicked');
              navigate(routes.symptomTracking.route);
            }
          }),
          LocalNotifications.addListener('localNotificationActionPerformed', notificationAction => {
            if (notificationAction.notification.actionTypeId === ActionTypes.PREVENTION_JOURNEY) {
              const id = notificationAction.notification.extra?.articleId ?? 1;
              const index = String(Math.max(1, id));
              navigate(routes.article.route.replace(':index', index));
              posthog.capture('prevention.article_reminder_notification_clicked', { articleIndex: index });
            }
          }),
        );
      }
      // Create Android notification channels (no-op on iOS/web and when flags are off)
      createNotificationChannels();

      if (Capacitor.isPluginAvailable('PushNotifications')) {
        // On success, we should be able to receive notifications
        const registrationListener = PushNotifications.addListener('registration', (token: Token) => {
          registerFCMToken(token.value);
          // On iOS, clearing delivered notifications also zeroes the OS badge
          // as a side effect (UIApplication.applicationIconBadgeNumber = 0),
          // so resync it afterwards or a real unread count gets wiped out
          // every cold start (registration only re-fires on cold start, not
          // on a background/foreground resume, which is why the badge would
          // disappear on cold-open but not when merely resuming the app).
          PushNotifications.removeAllDeliveredNotifications().then(() => syncBadgeCount());
          scheduleArticleReminders();
        });

        const actionPerformedListener = PushNotifications.addListener(
          'pushNotificationActionPerformed',
          notificationAction => {
            const data = (notificationAction.notification as any)?.data;
            const pushNotificationId = data?.notification_id;
            const pushNotificationType = data?.notification_type;
            posthog.capture('push_notification.clicked', {
              notification_id: pushNotificationId,
              notification_type: pushNotificationType,
            });
            const navigateTo = data?.navigate_to as string | undefined;
            if (navigateTo) {
              navigate(navigateTo);
            }
          },
        );

        // Every push the server sends (new message, badge-only sync, reassignment, ...)
        // reflects a badge_count change. While the app is in the foreground, iOS/Android
        // deliver these to the app instead of presenting/applying them at the OS level
        // (this is the notification handed to us here, alert or silent), so without this
        // listener the OS badge would only catch up the next time the app is resumed.
        const receivedListener = PushNotifications.addListener('pushNotificationReceived', () => {
          syncBadgeCount();
        });

        pushNotificationListenerHandles.push(registrationListener, actionPerformedListener, receivedListener);
      }
    }

    return () => {
      localNotificationListenerHandles.forEach(handle => handle.then(l => l.remove()));
      pushNotificationListenerHandles.forEach(handle => handle.then(l => l.remove()));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, authStatus, currentUserId]);

  // Re-register for push notifications whenever the app becomes visible. This
  // covers the case where the user enabled notifications from the OS settings
  // (e.g. after tapping the NotificationSuggester) and comes back to the app:
  // without this, the FCM token is never obtained/sent to the backend, so
  // server-side push notifications remain effectively disabled even though the
  // OS now allows them.
  useEffect(() => {
    if (
      isAppVisible &&
      authStatus === 'authenticated' &&
      accessToken &&
      currentUserId &&
      Platform.isNative() &&
      Capacitor.isPluginAvailable('PushNotifications') &&
      posthog.isFeatureEnabled('fcm-reregister-on-resume')
    ) {
      PushNotifications.checkPermissions().then(permissions => {
        if (permissions.receive === 'granted') {
          PushNotifications.register();
        }
      });
    }
  }, [isAppVisible, accessToken, authStatus, currentUserId]);

  // Resync the OS badge count whenever the app opens or comes back to the
  // foreground. Badge auto-clear is disabled (see capacitor.config.ts) so the
  // badge is otherwise only updated by silent pushes, which can be missed
  // while the app isn't running (killed, offline, notification not delivered).
  useEffect(() => {
    if (isAppVisible && authStatus === 'authenticated' && accessToken && currentUserId) {
      syncBadgeCount();
    }
  }, [isAppVisible, accessToken, authStatus, currentUserId]);

  useEffect(() => {
    if (authStatus !== 'authenticated' || !accessToken || !currentUserId) return;
    if (!Capacitor.isPluginAvailable('PushNotifications')) return;

    if (posthog.isFeatureEnabled(BETTER_NOTIFICATION_REQUEST_TIMING_FLAG)) {
      void PushNotifications.checkPermissions().then(result => {
        posthog.capture('$set', {
          $set: {
            gynger_push_notifications_permission: result.receive,
          },
        });
        if (result.receive === 'granted') {
          PushNotifications.register();
        }
      });
    } else {
      PushNotifications.requestPermissions().then(result => {
        posthog.capture('$set', {
          $set: {
            gynger_push_notifications_permission: result.receive,
          },
        });
        if (result.receive === 'granted') {
          PushNotifications.register();
        }
      });
    }
  }, [accessToken, authStatus, currentUserId]);

  // Schedule article read reminders each time the app is opened (cold start or from background)
  useEffect(() => {
    if (
      authStatus === 'authenticated' &&
      accessToken &&
      currentUserId &&
      Platform.isNative() &&
      Capacitor.isPluginAvailable('LocalNotifications')
    ) {
      scheduleArticleReminders();
      const listener = App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          scheduleArticleReminders();
        }
      });
      return () => {
        listener.then(l => l.remove());
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, accessToken, currentUserId]);

  useEffect(() => {
    if (authStatus === 'authenticated' && accessToken && currentUserId) {
      // `active` gates every side-effect inside onMessage/onClose.
      // React Strict Mode mounts effects twice, leaving the first WebSocket
      // alive server-side long enough to receive the same messages as the
      // second one. Setting active=false in the cleanup silently drops those
      // stale deliveries without relying on the async WS close handshake.
      let active = true;

      // Apply a WebSocket-driven change to a conversation's message cache.
      //
      // `setQueryData` on its own is not enough. The messages query is a plain
      // `GET /doctor-conversations/<id>/`, and one that was already in flight
      // when the event arrived resolves with a snapshot taken *before* the
      // change and overwrites what we wrote here — the message (or edit, or
      // deletion) then stays invisible until something else happens to
      // refetch. Re-entering a conversation fires such a request, so the
      // window is open exactly when a caregiver is most likely to be sent
      // something: accepting a reply suggestion could leave them looking at a
      // conversation that does not contain the message they just sent.
      //
      // Invalidating while a fetch is in flight cancels it (`cancelRefetch`
      // defaults to true) and starts one that can see the change, so the list
      // converges instead of silently losing it. Outside that window this is a
      // no-op and the `setQueryData` above stands on its own.
      const applyToMessagesCache = (
        conversationId: string,
        updater: (prev: ConversationMessage[] | undefined) => ConversationMessage[] | undefined,
      ) => {
        const queryKey = conversationMessagesQueryKey(conversationId);
        queryClient.setQueryData<ConversationMessage[]>(queryKey, updater);
        if (queryClient.getQueryState(queryKey)?.fetchStatus === 'fetching') {
          void queryClient.invalidateQueries({ queryKey });
        }
      };

      WebSocketService.getNewInstance({
        accessToken,
        onOpen: () => dispatch(conversationActions.setWebsocketConnectionStatus({ connected: true })),
        onClose: () => {
          if (active) dispatch(conversationActions.setWebsocketConnectionStatus({ connected: false }));
        },
        onMessage: data => {
          if (!active) return;
          switch (data.type) {
            case 'chat_message_edited':
              const payloadEdited = data.payload as WebSocketDataMessageEdited;
              applyToMessagesCache(payloadEdited.conversation, prev => {
                if (!prev) return prev;
                return prev.map(m =>
                  m.id === payloadEdited.id
                    ? { ...m, message_text: payloadEdited.message_text, edited_at: payloadEdited.edited_at }
                    : m,
                );
              });
              updateConversations(
                updateConversationLastMessageIfMatch(payloadEdited.conversation, payloadEdited.id, {
                  message_text: payloadEdited.message_text,
                  edited_at: payloadEdited.edited_at,
                }),
              );
              break;

            case 'chat_message_deleted':
              const payloadDeleted = data.payload as WebSocketDataMessageDeleted;
              applyToMessagesCache(payloadDeleted.conversation, prev => {
                if (!prev) return prev;
                return prev.map(m => {
                  if (m.id === payloadDeleted.id) return { ...m, is_deleted: true };
                  if (m.replied_to_message?.id === payloadDeleted.id)
                    return { ...m, replied_to_message: { ...m.replied_to_message, is_deleted: true } };
                  return m;
                });
              });
              updateConversations(
                updateConversationLastMessageIfMatch(payloadDeleted.conversation, payloadDeleted.id, {
                  message_text: '',
                  is_deleted: true,
                }),
              );
              break;

            // A message sent in a conversation in which the current user is directly
            // involved (== either the patient or the current_owner). Updates the row's
            // last_message and bumps unread; no-op if the row isn't in the cache yet
            // (which is fine — `conversation.created_by_patient`/`became_visible`/`unassigned_message`
            // are responsible for inserting it before this event fires).
            case 'chat_message':
              const payload = data.payload as WebSocketDataChatMessage;
              updateConversations(
                updateConversationWithNewMessage({
                  conversationId: payload.conversation,
                  sender: payload.sender,
                  messageText: payload.message_text,
                  messageId: payload.id,
                  incrementUnreadMessages: true,
                }),
              );
              // Append to the TanStack messages cache if the conversation is open.
              // The `if (!prev) return prev` guard is intentional: we must not create
              // a cache entry here — if the messages query hasn't finished loading yet
              // the conversation page disables the send button, so the only WS events
              // that can arrive before load are from other participants, and those will
              // be gap-recovered by the invalidation on mount / WS reconnect.
              applyToMessagesCache(payload.conversation, prev => {
                if (!prev) return prev;
                if (prev.some(m => m.id === payload.id)) return prev;
                return [
                  ...prev,
                  {
                    id: payload.id,
                    message_text: payload.message_text,
                    sender: payload.sender,
                    sent_at: payload.sent_at,
                    replied_to_message: payload.replied_to_message,
                  },
                ];
              });
              if (payload.sender?.id !== currentUserId) {
                window.dispatchEvent(
                  new CustomEvent<InAppChatNotificationDetail>(IN_APP_CHAT_NOTIFICATION_EVENT, {
                    detail: {
                      type: 'chat_message',
                      conversationId: payload.conversation,
                      senderDisplayName: payload.sender?.display_name,
                      senderPhotoUrl: payload.sender?.photo_url,
                      messageText: payload.message_text,
                    },
                  }),
                );
              }
              break;

            // a message has been read
            case 'conversation.read_notification':
              const payloadReadNotification = data.payload as WebSocketDataReadNotification;
              updateConversations(
                updateConversationReadStatusMutator({
                  conversationId: payloadReadNotification.conversation,
                  reader: payloadReadNotification.reader,
                  lastReadDatetime: payloadReadNotification.last_read_datetime,
                }),
              );
              break;

            // a conversation's status has been updated
            case 'conversation.status.updated':
              const payloadStatusUpdate = data.payload as WebSocketDataConversationStatusUpdated;
              updateConversations(setConversationStatusValue(payloadStatusUpdate.id, payloadStatusUpdate.new_status));
              break;

            // A conversation has been requested to be transferred to a group or a
            // caregiver (or reverted back to first-come-first-served after a
            // caregiver rejected or timed out on a specific assignment). We simply
            // upsert the row coming from the server — its `status` field already
            // determines the segmented bucket (unassigned / myRequest /
            // waitingForReassignment) via `getConversationType`, so we don't need to
            // track that bucket separately. This upsert (filter-by-id then prepend)
            // is idempotent, so it's safe whether this arrives before, after, or
            // instead of a local optimistic update (e.g. from rejectAssignment).
            case 'conversation.assignment.asked_group':
            case 'conversation.assignment.asked_caregiver':
            case 'conversation.assignment.rejected':
            case 'conversation.assignment.timed_out':
              const payloadWaitingForAssignmentMessage = data.payload as WebSocketDataConversationRequestToGroup;
              updateConversations(previous => {
                const filtered = previous.filter(c => c.id !== payloadWaitingForAssignmentMessage.id);
                return [payloadWaitingForAssignmentMessage, ...filtered];
              });
              break;

            // A message sent in a conversation in which the current_owner is null —
            // delivered to all first-line caregivers so their inbox can pick it up
            // without a refetch.
            case 'unassigned_message':
              const payloadUnassignedMessage = data.payload as WebSocketDataChatMessage;
              if (payloadUnassignedMessage.sender.id !== currentUserId) {
                updateConversations(
                  insertUnassignedConversationFromMessage({
                    conversationId: payloadUnassignedMessage.conversation,
                    sender: payloadUnassignedMessage.sender,
                    messageText: payloadUnassignedMessage.message_text,
                    repliedToMessage: payloadUnassignedMessage.replied_to_message,
                    messageId: payloadUnassignedMessage.id,
                  }),
                );
                // Keep the caregiver's open ConversationPage in sync if they happen
                // to be viewing this unassigned conversation.
                applyToMessagesCache(payloadUnassignedMessage.conversation, prev => {
                  if (!prev) return prev;
                  if (prev.some(m => m.id === payloadUnassignedMessage.id)) return prev;
                  return [
                    ...prev,
                    {
                      id: payloadUnassignedMessage.id,
                      message_text: payloadUnassignedMessage.message_text,
                      sender: payloadUnassignedMessage.sender,
                      sent_at: payloadUnassignedMessage.sent_at,
                      replied_to_message: payloadUnassignedMessage.replied_to_message,
                    },
                  ];
                });
                window.dispatchEvent(
                  new CustomEvent<InAppChatNotificationDetail>(IN_APP_CHAT_NOTIFICATION_EVENT, {
                    detail: {
                      type: 'unassigned_message',
                      conversationId: payloadUnassignedMessage.conversation,
                      senderDisplayName: payloadUnassignedMessage.sender?.display_name,
                      senderPhotoUrl: payloadUnassignedMessage.sender?.photo_url,
                      messageText: payloadUnassignedMessage.message_text,
                    },
                  }),
                );
              }
              break;

            // A conversation has been self-assigned by a caregiver. For caregivers
            // who could have picked it up: the row disappears from their inbox (unless
            // they are the new owner, in which case it's marked ASSIGNED_TO_CAREGIVER).
            // For the patient: update current_owner so the top bar immediately shows
            // the caregiver's name (`current_owner` UserDTO is included in the
            // patient-facing variant of this event).
            case 'conversation_assigned':
              const payloadConversationAssigned = data.payload as WebSocketDataConversationAssigned;
              if (payloadConversationAssigned.current_owner) {
                updateConversations(
                  setOwnerForConversationMutator(
                    payloadConversationAssigned.id,
                    payloadConversationAssigned.current_owner,
                  ),
                );
              } else if (payloadConversationAssigned.new_owner !== currentUserId) {
                updateConversations(removeConversationMutator(payloadConversationAssigned.id));
                // If this caregiver is currently viewing the conversation that was
                // just taken by someone else, redirect them back to the inbox and
                // show a toast so they know what happened.
                const takenConversationPath = routes.conversation.route.replace(
                  ':conversationId',
                  payloadConversationAssigned.id,
                );
                if (window.location.pathname.startsWith(takenConversationPath)) {
                  navigate(routes.messagingHome.route);
                  toast(t('chats.takenOverNotification.title'), {
                    id: `taken-over-${payloadConversationAssigned.id}`,
                    description: t('chats.takenOverNotification.body'),
                    position: 'top-center',
                    duration: 5000,
                  });
                }
              } else {
                updateConversations(
                  setConversationStatusValue(payloadConversationAssigned.id, 'ASSIGNED_TO_CAREGIVER'),
                );
              }
              break;

            // Patient's own conversation was created — insert it into the list and
            // navigate into it so the user lands on the conversation page regardless of
            // whether the HTTP response has returned yet.
            case 'conversation.created_by_patient':
              const createdConversation = data.payload as WebSocketDataConversationCreated;
              updateConversations(insertConversation(createdConversation));
              navigate(routes.conversation.route.replace(':conversationId', createdConversation.id));
              break;

            // A conversation has just become visible to the patient (typically: they
            // were oriented to a different group, the new caregiver picked it up, and
            // just sent the first message). Idempotent upsert into the cache; unlike
            // `conversation.created_by_patient`, no navigation — the patient might be
            // looking at another conversation right now.
            case 'conversation.became_visible':
              const appearedConversation = data.payload as WebSocketDataConversationCreated;
              updateConversations(insertConversation(appearedConversation));
              break;

            // A patient just completed their questionnaire and the backend assigned
            // (or re-assigned) a conversation to this caregiver.  The payload is a
            // full DoctorConversationDTO row — upsert it so the questionnaire_needs_review
            // badge appears immediately without a re-fetch.
            case 'questionnaire.completed':
              const completedQConversation = data.payload as WebSocketDataConversationCreated;
              updateConversations(upsertConversation(completedQConversation));
              break;

            // The review phase changed (started or finished).  Sent to the patient only.
            // Update the cached questionnaire response so the homepage card transitions
            // to 'in_review' or 'reviewed' without a page reload.
            case 'questionnaire.status_updated':
              queryClient.setQueryData(['questionnaire', 'myResponse', currentUserId], data.payload);
              break;

            // A candidate reply suggestion (GYN-266) is ready for a conversation this
            // caregiver currently owns. Write it straight into the suggestedAnswer cache
            // entry ConversationPage reads from, so it appears without a fetch.
            case 'reply_suggestion.created':
              const suggestionPayload = data.payload as WebSocketDataReplySuggestionCreated;
              queryClient.setQueryData(suggestedAnswerQueryKey(suggestionPayload.conversation), {
                id: suggestionPayload.id,
                suggestion: suggestionPayload.suggestion,
              });
              break;

            default:
              break;
          }
        },
      });

      return () => {
        active = false;
        WebSocketService.close();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, authStatus, currentUserId]);

  if (authStatus === 'loading' || !currentUser) {
    return <PageLoader />;
  }

  return (
    <div className="flex h-app-screen flex-col bg-card">
      {/* `<main>`: the functional suite scopes most of its assertions to `get_by_role("main")`. */}
      <main id="content" className="m-0 h-full min-h-0 flex-auto overflow-hidden p-0">
        <PushNotificationOptInProvider>
          <Outlet />
        </PushNotificationOptInProvider>
      </main>
      {!keyboardVisible && <BottomNavBar />}

      {/* Track caregiver activity time */}
      {currentUser?.is_caregiver && currentUserId && <ActivityTracker userId={currentUserId} />}
    </div>
  );
}
